import React, { useState, useEffect } from 'react';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import UserList from '../components/user/UserList';
import { useAuth, type Account } from '../contexts/AuthContext';
import { PostJSON } from '../utils/request';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEmails(value: string[]): string | null {
  if (value.length === 0) return null;
  const invalid = value.some(e => !EMAIL_REGEX.test(e.trim()));
  return invalid ? 'All entries must be valid email addresses' : null;
}

function emailsToArray(value: string): string[] {
  return value ? value.split(',').map(e => e.trim()).filter(Boolean) : [];
}

function emailsToString(value: string[]): string {
  return value.map(e => e.trim()).filter(Boolean).join(', ');
}

interface ConfigsFormValues {
  checkinUpdateEmail: string[];
  checkinWeeklyDigestEmail: string[];
  checkinWeeklyDigestDayOfWeek: string; // Store as string for Select component
  clientDigestCopyEmail: string[];
}

interface PhoneFormValues {
  preferredAreaCodes: string[];
}

interface TermsFormValues {
  aide: string;
}

const ManageAccount: React.FC = () => {
  const { user, account, refreshUser } = useAuth();
  const [isUpdatingConfigs, setIsUpdatingConfigs] = useState(false);
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [isUpdatingTerms, setIsUpdatingTerms] = useState(false);
  const [configsError, setConfigsError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);

  // Configs form
  const configsForm = useForm<ConfigsFormValues>({
    initialValues: {
      checkinUpdateEmail: emailsToArray(account?.configs?.checkinUpdateEmail || ''),
      checkinWeeklyDigestEmail: emailsToArray(account?.configs?.checkinWeeklyDigestEmail || ''),
      checkinWeeklyDigestDayOfWeek: String(account?.configs?.checkinWeeklyDigestDayOfWeek || 1),
      clientDigestCopyEmail: emailsToArray(account?.configs?.clientDigestCopyEmail || '')
    },
    validate: {
      checkinUpdateEmail: (value) => validateEmails(value),
      checkinWeeklyDigestEmail: (value) => validateEmails(value),
      clientDigestCopyEmail: (value) => validateEmails(value),
    }
  });

  // Phone settings form
  const phoneForm = useForm<PhoneFormValues>({
    initialValues: {
      preferredAreaCodes: account?.configs?.preferredAreaCodes || []
    },
    validate: {
      preferredAreaCodes: (value) => {
        if (value.length === 0) return null;
        const invalid = value.some(code => !/^\d{3}$/.test(code));
        return invalid ? 'All entries must be 3-digit area codes' : null;
      }
    }
  });

  // Terms form
  const termsForm = useForm<TermsFormValues>({
    initialValues: {
      aide: account?.configs?.terms?.aide || ''
    }
  });

  // Update configs form values when account data changes
  useEffect(() => {
    if (account) {
      configsForm.setValues({
        checkinUpdateEmail: emailsToArray(account.configs?.checkinUpdateEmail || ''),
        checkinWeeklyDigestEmail: emailsToArray(account.configs?.checkinWeeklyDigestEmail || ''),
        checkinWeeklyDigestDayOfWeek: String(account.configs?.checkinWeeklyDigestDayOfWeek || 1),
        clientDigestCopyEmail: emailsToArray(account.configs?.clientDigestCopyEmail || '')
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // Update phone form values when account data changes
  useEffect(() => {
    if (account) {
      phoneForm.setValues({
        preferredAreaCodes: account.configs?.preferredAreaCodes || []
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // Update terms form values when account data changes
  useEffect(() => {
    if (account) {
      termsForm.setValues({
        aide: account.configs?.terms?.aide || ''
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const handleConfigsUpdate = async (values: ConfigsFormValues) => {
    setIsUpdatingConfigs(true);
    setConfigsError(null);

    try {
      const response = await PostJSON<{ success: boolean; account: Account }>('/api/account/update_account_configs', {
        configs: {
          checkinUpdateEmail: emailsToString(values.checkinUpdateEmail),
          checkinWeeklyDigestEmail: emailsToString(values.checkinWeeklyDigestEmail),
          checkinWeeklyDigestDayOfWeek: parseInt(values.checkinWeeklyDigestDayOfWeek, 10),
          clientDigestCopyEmail: emailsToString(values.clientDigestCopyEmail)
        }
      });

      if (response.success) {
        await refreshUser();
        notifications.show({
          title: 'Success',
          message: 'Notification settings updated successfully!',
          color: 'green'
        });
      } else {
        throw new Error('Failed to update notification settings');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update notification settings';
      setConfigsError(errorMessage);
    } finally {
      setIsUpdatingConfigs(false);
    }
  };

  const handlePhoneUpdate = async (values: PhoneFormValues) => {
    setIsUpdatingPhone(true);
    setPhoneError(null);

    try {
      const response = await PostJSON<{ success: boolean; account: Account }>('/api/account/update_account_configs', {
        configs: {
          preferredAreaCodes: values.preferredAreaCodes
        }
      });

      if (response.success) {
        await refreshUser();
        notifications.show({
          title: 'Success',
          message: 'Phone settings updated successfully!',
          color: 'green'
        });
      } else {
        throw new Error('Failed to update phone settings');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update phone settings';
      setPhoneError(errorMessage);
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const handleTermsUpdate = async (values: TermsFormValues) => {
    setIsUpdatingTerms(true);
    setTermsError(null);

    try {
      const response = await PostJSON<{ success: boolean; account: Account }>('/api/account/update_account_configs', {
        configs: {
          terms: {
            aide: values.aide.trim() || undefined
          }
        }
      });

      if (response.success) {
        await refreshUser();
        notifications.show({
          title: 'Success',
          message: 'Terminology updated successfully!',
          color: 'green'
        });
      } else {
        throw new Error('Failed to update terminology');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update terminology';
      setTermsError(errorMessage);
    } finally {
      setIsUpdatingTerms(false);
    }
  };

  return (
    <Stack gap="xl">
      <div>
        <Title order={1}>Manage Account</Title>
        {account && (
          <Title c="dimmed" fw={400} mt="xs" order={3}>
            {account.name}
          </Title>
        )}
      </div>

      <Paper p="xl" radius="md" shadow="sm">
        <UserList
          title="Account Users"
        />
      </Paper>

      {/* Notification Settings Section (admin only) */}
      {user?.isAdmin && (
        <Paper p="xl" radius="md" shadow="sm">
          <Stack gap="md">
            <div>
              <Title mb="xs" order={3}>Notification Settings</Title>
              <Text c="dimmed" size="sm">Configure email addresses for account notifications</Text>
            </div>

            <form onSubmit={configsForm.onSubmit(handleConfigsUpdate)}>
              <Stack gap="md">
                <TagsInput
                  description="Receive an email when a check-in is completed"
                  label="Check-in Update Email"
                  placeholder="Multiple emails can be entered by pressing Tab"
                  {...configsForm.getInputProps('checkinUpdateEmail')}
                />

                <TagsInput
                  description="Receive a weekly digest of check-in activity"
                  label="Check-in Weekly Digest Email"
                  placeholder="Multiple emails can be entered by pressing Tab"
                  {...configsForm.getInputProps('checkinWeeklyDigestEmail')}
                />

                <TagsInput
                  description="Additional emails copied on client-specific care digest emails"
                  label="Client Digest Copy Email"
                  placeholder="Multiple emails can be entered by pressing Tab"
                  {...configsForm.getInputProps('clientDigestCopyEmail')}
                />

                <Select
                  data={[
                    { value: '1', label: 'Monday' },
                    { value: '2', label: 'Tuesday' },
                    { value: '3', label: 'Wednesday' },
                    { value: '4', label: 'Thursday' },
                    { value: '5', label: 'Friday' },
                    { value: '6', label: 'Saturday' },
                    { value: '0', label: 'Sunday' }
                  ]}
                  description="Day of the week to send the weekly digest (always at 9:00 AM)"
                  label="Weekly Digest Day"
                  {...configsForm.getInputProps('checkinWeeklyDigestDayOfWeek')}
                />

                {configsError && (
                  <Alert
                    color="red"
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    title="Error"
                    variant="light"
                  >
                    {configsError}
                  </Alert>
                )}

                <Group justify="flex-end">
                  <Button
                    disabled={isUpdatingConfigs}
                    loading={isUpdatingConfigs}
                    type="submit"
                  >
                    {isUpdatingConfigs ? 'Updating...' : 'Update Notifications'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Paper>
      )}

      {/* Phone Settings Section (admin only) */}
      {user?.isAdmin && (
        <Paper p="xl" radius="md" shadow="sm">
          <Stack gap="md">
            <div>
              <Title mb="xs" order={3}>Phone Settings</Title>
              <Text c="dimmed" size="sm">Configure preferred area codes for purchasing new phone numbers</Text>
            </div>

            <form onSubmit={phoneForm.onSubmit(handlePhoneUpdate)}>
              <Stack gap="md">
                <TagsInput
                  description="Area codes used when purchasing new phone numbers (e.g. 212, 718). Press Tab to add each code."
                  label="Preferred Area Codes"
                  placeholder="Enter a 3-digit area code"
                  {...phoneForm.getInputProps('preferredAreaCodes')}
                />

                {phoneError && (
                  <Alert
                    color="red"
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    title="Error"
                    variant="light"
                  >
                    {phoneError}
                  </Alert>
                )}

                <Group justify="flex-end">
                  <Button
                    disabled={isUpdatingPhone}
                    loading={isUpdatingPhone}
                    type="submit"
                  >
                    {isUpdatingPhone ? 'Updating...' : 'Update Phone Settings'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Paper>
      )}

      {/* Terminology Section (admin only) */}
      {user?.isAdmin && (
        <Paper p="xl" radius="md" shadow="sm">
          <Stack gap="md">
            <div>
              <Title mb="xs" order={3}>Terminology</Title>
              <Text c="dimmed" size="sm">Customize the terms used throughout the platform to match your organization</Text>
            </div>

            <form onSubmit={termsForm.onSubmit(handleTermsUpdate)}>
              <Stack gap="md">
                <TextInput
                  description={'Custom term to use instead of "aide" (e.g. caregiver, worker, attendant). Leave blank to use the default.'}
                  label="Aide"
                  placeholder="aide"
                  {...termsForm.getInputProps('aide')}
                />

                {termsError && (
                  <Alert
                    color="red"
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    title="Error"
                    variant="light"
                  >
                    {termsError}
                  </Alert>
                )}

                <Group justify="flex-end">
                  <Button
                    disabled={isUpdatingTerms}
                    loading={isUpdatingTerms}
                    type="submit"
                  >
                    {isUpdatingTerms ? 'Updating...' : 'Update Terminology'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default ManageAccount;
